import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as yup from "yup";
import "./index.scss";
import {
  Autocomplete,
  Button,
  CircularProgress,
  createFilterOptions,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import JobTitle from "../job-title";
import { AddContactForm, CompanyManager } from "../../../config/interfaces";
import {
  useAddUserProjectMutation,
  useGetCompanyManagerListLazyQuery,
} from "../../../../graphql/generated/graphql";
import { useProjectsState } from "../../../hooks/projectsInfo";
import { setAlertDescription } from "../../../hooks/actions";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 500,
  bgcolor: "#fff",
  boxShadow: 24,
  borderRadius: "20px",
  p: 4,
  pb: 4,
};

const initialValues: AddContactForm = {
  name: "",
  email: "",
  jobTitle: "",
  phone: '',
};

const AddContactSchema = yup.object().shape({
  name: yup
    .string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  email: yup.string().email("Invalid email").required("Required"),
  jobTitle: yup.string().required("Required"),
});

const CrewModal = (props: any) => {
  const { open, onClose, refetch } = props;
  const currentProject = useProjectsState().projectId.get() || "";
  const [loader, setLoader] = useState(false);
  const [openInfoContact, setOpenInfoContact] = useState(false);
  const [disabledField, setDisabledField] = useState(false)
  const [toCleanJobTitle, setToCleanJobTitle] = useState<boolean>(false);
  const nameRef = useRef<any>()
  const clearFieldName = () => {
    if (nameRef.current.querySelector('button')) {
      const buttonClean = nameRef.current.querySelector('button')
      buttonClean.click();
    }
  }
  const [options, setOptions] = useState<CompanyManager[]>([]);
  const [valueInfoUser, setValueInfoUser] = useState<
    CompanyManager | null | undefined
  >();
  const [addUserProject] = useAddUserProjectMutation();
  const [
    getCompanyManager,
    { data: listCompanyManagers, loading: loadingSearch },
  ] = useGetCompanyManagerListLazyQuery();

  const handleClose = () => {
    onClose();
    formik.resetForm();
    setDisabledField(false);
    setValueInfoUser(null);
  };

  useEffect(() => {
    if (listCompanyManagers) {
      setOptions(listCompanyManagers.getCompanyManagerList as CompanyManager[]);
    }
  }, [listCompanyManagers]);

  const formik = useFormik({
    initialValues,
    validationSchema: AddContactSchema,
    onSubmit: async (variables) => {
      setLoader(true);
      try {
        await addUserProject({
          variables: {
            project_id: currentProject,
            users: [
              {
                email: variables.email,
                name: variables.name,
                job_title_id: variables.jobTitle,
                phone: variables.phone
              },
            ],
          },
        }).then(() => {
          setAlertDescription({
            open: true,
            message: "The user has been added to the project",
            severity: "success",
          });
          refetch();
          handleClose();
        });
      } catch (e) {
        console.error(e);
      }
      setLoader(false);
    },
  });

  const onChangeSearchUserHandle = async (value: string) => {
    formik.setFieldValue("name", value);
    if (value.length > 2) {
      getCompanyManager({
        variables: {
          name_or_email: value,
        },
      });
    } else {
      getCompanyManager({
        variables: {
          name_or_email: "/",
        },
      });
    }
  };

  useEffect(() => {
    if (!disabledField && nameRef.current) {
      console.log('name', nameRef)
      clearFieldName();
    }
  }, [disabledField])

  //set ready to another clear to jobtitle
  useEffect(() => {
    if (toCleanJobTitle) {
      setToCleanJobTitle(false)
    }
  }, [toCleanJobTitle])

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
          className="form-crew"
        >
          <>
            <Typography
              component="h2"
              variant="body1"
              className="form-crew-title"
            >
              Create new contact
            </Typography>
            <Grid className="container-fields">
              <>
                <FormControl
                  component="form"
                  onSubmit={formik.handleSubmit}
                  className="form-crew-form"
                >
                  <Autocomplete
                    id="infoContacts"
                    ref={nameRef}
                    freeSolo
                    className="autocomplete form-crew-autocomplete"
                    open={openInfoContact}
                    options={options}
                    onOpen={() => {
                      setOpenInfoContact(true);
                    }}
                    onClose={() => {
                      setOpenInfoContact(false);
                    }}
                    disabled={disabledField}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {`${option.full_name} - ${option.email}`}
                      </li>
                    )}
                    value={valueInfoUser}
                    onChange={(
                      event: any,
                      newValue: CompanyManager | null | undefined | string
                    ) => {
                      if (typeof newValue === "string") {
                        formik.setFieldValue("name", newValue);
                        setValueInfoUser({
                          full_name: newValue,
                          email: "",
                        } as CompanyManager);
                      } else if (newValue && newValue.email) {
                        setValueInfoUser(newValue as CompanyManager);
                        formik.setFieldValue("email", newValue?.email);
                        formik.setFieldValue("name", newValue?.full_name);
                        formik.setFieldValue("phone", newValue?.phone);
                        setDisabledField(true);
                      } else if (newValue) {
                        setValueInfoUser(newValue as CompanyManager);
                        formik.setFieldValue("name", newValue?.first_name);
                      }
                    }}
                    filterOptions={(optionsFilter, params: any) => {
                      const filtered = optionsFilter as CompanyManager[];
                      const { inputValue } = params;
                      // Suggest the creation of a new value
                      const isExisting = optionsFilter.some(
                        (option) => inputValue === option.full_name
                      );
                      if (inputValue !== "" && !isExisting) {
                        filtered.push({
                          first_name: inputValue,
                          full_name: `Add user "${inputValue}"`,
                          email: "",
                        } as CompanyManager);
                      }
                      return filtered;
                    }}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") {
                        return option;
                      }
                      if (option.full_name.includes("Add user")) {
                        return `${option.first_name}`;
                      }
                      return `${option.full_name}`;
                    }}
                    loading={loadingSearch}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        className="textfield"
                        placeholder="Search a co-worker by name or add a new one"
                        onChange={(ev) => {
                          if (
                            ev.target.value !== "" ||
                            ev.target.value !== null
                          ) {
                            onChangeSearchUserHandle(ev.target.value);
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingSearch ? (
                                <CircularProgress
                                  color="primary"
                                  size={20}
                                />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    required
                    id="email"
                    label="Email"
                    name="email"
                    className="textfield"
                    autoComplete="email"
                    disabled={disabledField}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                  <TextField
                    required
                    id="phone"
                    label="Phone"
                    name="phone"
                    className='textfield'
                    autoComplete="phone"
                    disabled={disabledField}
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                  <FormControl className="form-jobtitle">
                    <JobTitle
                      clear={toCleanJobTitle}
                      value={formik.values.jobTitle}
                      formik={formik}
                      error={
                        formik.touched.jobTitle &&
                        Boolean(formik.errors.jobTitle)
                      }
                      helperText={
                        formik.touched.jobTitle && formik.errors.jobTitle
                      }
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ my: 1 }}
                  >
                    {loader ? (
                      <CircularProgress
                        size={20}
                        color="secondary"
                      />
                    ) : (
                      "Send"
                    )}
                  </Button>
                  <Button
                    color='inherit'
                    variant="contained"
                    sx={{ my: 1 }}
                    onClick={() => {
                      formik.resetForm();
                      setToCleanJobTitle(true);
                      setDisabledField(false);
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="reset"
                    color="secondary"
                    variant="contained"
                    onClick={handleClose}
                    sx={{ my: 1 }}
                  >
                    Cancel
                  </Button>
                </FormControl>
              </>
            </Grid>
          </>
        </Box>
      </Box>
    </Modal>
  );
};
export default CrewModal;
